// Composant Radar Chart Management (Q2) avec Recharts
// 6 axes : Orientations, Roles et missions, Gestion des moyens, Animation, Bilan chantier, Audits
// Echelle normalisee 0-4 pour equilibrer visuellement les axes
// Axes N/A (max=0) filtres automatiquement

import {
  RadarChart as RechartsRadar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
  Tooltip,
} from 'recharts'
import { Progress } from '@/components/ui/progress'

// Echelle fixe du radar Management : 0 a 4
const RADAR_MAX_SCALE = 4

// Les 6 axes du radar Management Q2
export const MANAGEMENT_AXES = [
  { key: 'orientations', code: 'O_orientations', label: 'Orientations' },
  { key: 'roles', code: 'R_rôles et missions', label: 'Rôles et\nmissions' },
  { key: 'gestion', code: 'G_gestion des moyens et ressources', label: 'Gestion des\nmoyens' },
  { key: 'animation', code: 'A-animation', label: 'Animation' },
  { key: 'bilan', code: 'B-bilans chantier et annuel', label: 'Bilans\nchantier' },
  { key: 'audits', code: 'A_audits internes, visites chantier', label: 'Audits /\nVisites' },
] as const

export type ManagementAxeKey = typeof MANAGEMENT_AXES[number]['key']

export interface ScoresManagement {
  orientations: number
  roles: number
  gestion: number
  animation: number
  bilan: number
  audits: number
}

export interface RadarChartManagementProps {
  scores: ScoresManagement
  maxScores?: ScoresManagement
  showLabels?: boolean
  height?: number
}

// Fallback barres horizontales si < 3 axes evaluables
function FallbackBars({ data }: { data: Array<{ subject: string; rawScore: number; max: number }> }) {
  return (
    <div className="space-y-3 py-4">
      {data.map((item) => {
        const percent = item.max > 0 ? (item.rawScore / item.max) * 100 : 0
        return (
          <div key={item.subject}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-foreground">{item.subject.replace('\n', ' ')}</span>
              <span className="text-xs text-muted-foreground">{item.rawScore}/{item.max}</span>
            </div>
            <Progress value={percent} className="h-2" />
          </div>
        )
      })}
    </div>
  )
}

export default function RadarChartManagement({
  scores,
  maxScores,
  showLabels = true,
  height = 300,
}: RadarChartManagementProps) {
  // Filtrer les axes dont max > 0 (evaluables uniquement)
  const activeAxes = MANAGEMENT_AXES.filter((axe) => {
    const max = maxScores?.[axe.key] || 0
    return max > 0
  })

  // Construire les donnees uniquement pour les axes actifs
  const data = activeAxes.map((axe) => {
    const rawScore = scores[axe.key] || 0
    const max = maxScores?.[axe.key] || 1
    const normalizedScore = (rawScore / max) * RADAR_MAX_SCALE
    return {
      subject: axe.label,
      score: Math.round(normalizedScore * 10) / 10,
      rawScore,
      max,
      fullMark: RADAR_MAX_SCALE,
    }
  })

  // Pas assez d'axes pour un radar (< 3) → fallback en barres
  if (data.length < 3) {
    return <FallbackBars data={data} />
  }

  const radarColor = 'var(--primary)'

  return (
    <div style={{ width: '100%', height }}>
      <ResponsiveContainer>
        <RechartsRadar
          data={data}
          margin={{ top: 20, right: 40, bottom: 20, left: 40 }}
        >
          <PolarGrid
            stroke="var(--border)"
            strokeWidth={1}
            gridType="polygon"
          />
          <PolarAngleAxis
            dataKey="subject"
            tick={({ x, y, payload }: any) => (
              <text
                x={x}
                y={y}
                textAnchor="middle"
                dominantBaseline="central"
                fill="var(--muted-foreground)"
                style={{ fontSize: '11px', fontWeight: 500 }}
              >
                {payload.value.includes('\n') ? (
                  payload.value.split('\n').map((line: string, i: number) => (
                    <tspan key={i} x={x} dy={i === 0 ? 0 : 14}>
                      {line}
                    </tspan>
                  ))
                ) : (
                  payload.value
                )}
              </text>
            )}
          />
          <PolarRadiusAxis
            angle={90}
            domain={[0, RADAR_MAX_SCALE]}
            tick={false}
            axisLine={false}
          />
          <Radar
            name="Score"
            dataKey="score"
            stroke={radarColor}
            fill={radarColor}
            fillOpacity={0.35}
            strokeWidth={3.5}
            dot={{
              r: 4,
              fill: radarColor,
              stroke: 'var(--card)',
              strokeWidth: 2,
            }}
          />
          {showLabels && (
            <Tooltip
              formatter={(_value: any, _name: any, entry: any) => {
                const item = entry.payload
                return [
                  `${item.score} / ${item.fullMark}`,
                  item.subject.replace('\n', ' '),
                ]
              }}
              contentStyle={{
                backgroundColor: 'var(--card)',
                border: '1px solid var(--border)',
                borderRadius: '8px',
                fontSize: '13px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              }}
            />
          )}
        </RechartsRadar>
      </ResponsiveContainer>
    </div>
  )
}

// Composant legende personnalisee pour Management avec points
export function RadarLegendManagement({ scores, maxScores, compact = false }: { scores: ScoresManagement; maxScores?: ScoresManagement; compact?: boolean }) {
  const getScoreColor = (score: number, max: number) => {
    if (max === 0) return 'text-muted-foreground bg-muted'
    const ratio = score / max
    if (ratio >= 0.7) return 'text-primary bg-primary/20'
    if (ratio >= 0.4) return 'text-primary bg-primary/10'
    return 'text-primary bg-primary/5'
  }

  if (compact) {
    return (
      <div className="grid grid-cols-3 gap-1 mt-2">
        {MANAGEMENT_AXES.map((axe) => {
          const score = scores[axe.key] || 0
          const max = maxScores?.[axe.key] || 0
          return (
            <div
              key={axe.key}
              className={`py-1 px-1.5 rounded text-center ${getScoreColor(score, max)}`}
            >
              <div className="text-xs font-medium opacity-75">{axe.label.replace('\n', ' ')}</div>
              <div className="text-xs font-bold">{score}</div>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
      {MANAGEMENT_AXES.map((axe) => {
        const score = scores[axe.key] || 0
        const max = maxScores?.[axe.key] || 0
        return (
          <div
            key={axe.key}
            className={`p-3 rounded-lg ${getScoreColor(score, max)}`}
          >
            <div className="text-xs font-medium opacity-75">
              {axe.label.replace('\n', ' ')}
            </div>
            <div className="text-lg font-bold">
              {score}{max > 0 ? `/${max}` : ''} pts
            </div>
          </div>
        )
      })}
    </div>
  )
}
